<template>
  <div class="component" v-bind:class="componentClasses">
    <template
      v-if="
        type === 'text' ||
        type === 'password' ||
        type === 'email' ||
        type === 'number'
      "
    >
      <forms-text-field
        v-model="newValue"
        v-bind:id="id"
        v-bind:type="type"
        v-bind:label="label"
        v-bind:placeholder="configValue('placeholder')"
        v-bind:min="min"
        v-bind:max="max"
        v-bind:mask="configValue('mask')"
        v-bind:character-counter="configValue('character_counter')"
        v-bind:prefix="configValue('prefix')"
        v-bind:use-masked-value="configValue('useMaskedValue')"
        v-on:input="onChange"
      ></forms-text-field>
    </template>
    <template v-else-if="type === 'textarea'">
      <forms-text-area
        v-model="newValue"
        v-bind:id="id"
        v-bind:label="label"
        v-bind:placeholder="configValue('placeholder')"
        v-bind:min="min"
        v-bind:max="max"
        v-bind:mask="configValue('mask')"
        v-bind:use-masked-value="configValue('useMaskedValue')"
        v-on:input="onChange"
      ></forms-text-area>
    </template>
    <template v-else-if="type === 'select'">
      <forms-select
        v-model="newValue"
        v-bind:id="id"
        v-bind:label="label"
        v-bind:placeholder="configValue('placeholder')"
        v-bind:options="configValue('options')"
        v-on:input="onChange"
      ></forms-select>
    </template>
    <template v-else-if="type === 'checkbox'">
      <forms-checkbox
        v-model="newValue"
        v-bind:id="id"
        v-bind:label="label"
        v-bind:options="configValue('options')"
        v-on:input="onChange"
      ></forms-checkbox>
    </template>
    <template v-else-if="type === 'checkbox-single'">
      <forms-checkbox-single
        v-model="newValue"
        v-bind:id="id"
        v-bind:value="configValue('value')"
        v-bind:text="configValue('text')"
        v-bind:label="label"
        v-on:input="onChange"
        v-on:click="onClick"
      ></forms-checkbox-single>
    </template>
    <template v-else-if="type === 'radio'">
      <forms-radio
        v-model="newValue"
        v-bind:id="id"
        v-bind:label="label"
        v-bind:options="configValue('options')"
        v-bind:horizontal="configValue('horizontal')"
        v-on:input="onChange"
      ></forms-radio>
    </template>
    <template v-else-if="type === 'address'">
      <forms-address
        v-model="newValue"
        v-bind:label="label"
        v-on:input="onChange"
        v-bind:return-key="configValue('returnKey')"
        v-bind:placeholder="configValue('placeholder')"
      ></forms-address>
    </template>
    <template v-else-if="type === 'file'">
      <forms-file
        v-model="newValue"
        v-bind:id="id"
        v-bind:label="label"
        v-on:input="onChange"
        v-bind:placeholder="configValue('placeholder')"
        :accept="configValue('accept')"
      ></forms-file>
    </template>
    <template v-else-if="type === 'boolean'">
      <forms-boolean
        v-model="newValue"
        v-bind:id="id"
        v-bind:label="label"
        v-on:input="onChange"
        v-bind:description="configValue('description')"
      ></forms-boolean>
    </template>
    <template v-else-if="type === 'okta-password-policy'">
      <okta-password-policy :id="id"> </okta-password-policy>
    </template>
    <template v-else-if="type === 'html'">
      <html-component
        v-bind:content="configValue('content')"
        v-bind:sources="configValue('sources')"
      ></html-component>
    </template>
    <template v-else-if="type === 'stepper'">
      <stepper-component
        v-bind:steps="configValue('steps')"
        v-bind:finished-steps="configValue('finished-steps')"
        v-bind:vertical="configValue('vertical')"
      >
      </stepper-component>
    </template>
    <template v-else-if="type === 'stripe-credit-card'">
      <stripe-credit-card
        v-model="newValue"
        :id="id"
        :outlined="configValue('outlined')"
        :actionText="configValue('actionText')"
        :titleText="configValue('titleText')"
        :errors="errors"
        v-on:success="() => this.$emit('onPageButtonClick', config)"
        @input="onChange"
      >
      </stripe-credit-card>
    </template>
    <template v-else-if="type === 'button'">
      <div class="clearfix">
        <page-button
          v-bind:form-conditions="formConditions"
          v-bind:config="config"
          v-on:click="() => this.$emit('onPageButtonClick', config)"
        ></page-button>
      </div>
    </template>
    <template v-else-if="type === 'divider'">
      <hr />
    </template>
    <template v-else-if="type === 'order-status'">
      <order-status
        :orderId="configValue('orderId')"
        :orderType="configValue('orderType', 'shipper')"
        :token="configValue('token')"
      ></order-status>
    </template>
    <template v-else-if="type === 'order-cancellation-rationales-selector'">
      <order-cancellation-rationales-selector
        v-model="newValue"
        v-on:input="onChange"
        v-bind:token="configValue('token')"
      ></order-cancellation-rationales-selector>
    </template>
    <template v-else-if="type == 'delivery-schedule'">
      <delivery-schedule
        v-model="newValue"
        v-on:input="onChange"
        v-bind:type="configValue('scheduleType', 'pickup')"
      ></delivery-schedule>
    </template>
    <template v-else-if="type == 'schedule-pickup-dropoff'">
      <schedule-pickup-dropoff
        :name="configValue('scheduleType', 'Pickup')"
        v-model="newValue"
        v-on:input="onChange"
        :initial-header="configValue('initialHeader')"
        :show-hours="configValue('showHours', true)"
        :hide-operation-hours="configValue('hideOperationHours', true)"
      >
      </schedule-pickup-dropoff>
    </template>
    <ul
      v-if="type !== 'stripe-credit-card' && errors && errors.length > 0"
      class="errors"
    >
      <div v-if="isErrorsArray">
        <li v-for="(error, index) in errors" v-bind:key="index">
          {{ error }}
        </li>
      </div>
      <div v-else>
        <li>
          {{ errors }}
        </li>
      </div>
    </ul>
  </div>
</template>

<script type="text/babel">
import FormsTextField from "@/components/forms/text-field";
import FormsTextArea from "@/components/forms/text-area";
import FormsSelect from "@/components/forms/select";
import FormsCheckbox from "@/components/forms/checkbox";
import FormsCheckboxSingle from "@/components/forms/checkbox-single";
import FormsRadio from "@/components/forms/radio";
import FormsAddress from "@/components/forms/address";
import FormsFile from "@/components/forms/file";
import FormsBoolean from "@/components/forms/boolean";
import PageButton from "./button";
import OktaPasswordPolicy from "./okta-password-policy";
import HtmlComponent from "./html";
import StepperComponent from "./stepper";
import StripeCreditCard from "./stripe-credit-card";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import OrderStatus from "./order-status";
import OrderCancellationRationalesSelector from "./order-cancellation-rationales-selector";
import DeliverySchedule from "./delivery-schedule";
import SchedulePickupDropoff from "./schedule-pickup-dropoff";

export default {
  name: "components-json_page-components-model",
  watch: {
    value(newValue) {
      this.newValue = newValue;
    }
  },
  props: {
    id: undefined,
    value: undefined,
    config: {
      required: true
    },
    formConditions: {
      default() {
        return {};
      }
    },
    errors: {
      default() {
        return [];
      }
    }
  },
  data() {
    return {
      newValue: undefined
    };
  },
  methods: {
    configValue(key, fallback) {
      return _get(this.config, key, fallback);
    },
    onClick() {
      if (this.configValue("onClick")) {
        this.$emit("onPageButtonClick", this.configValue("onClick"));
      }

      if (this.type === "checkbox-single") {
        if (this.newValue) {
          this.$emit("onPageButtonClick", this.configValue("onCheckedAction"));
        } else {
          this.$emit(
            "onPageButtonClick",
            this.configValue("onUncheckedAction")
          );
        }
      }
    },
    onChange() {
      this.filter();

      if (this.configValue("onInput")) {
        this.$emit("onPageButtonClick", this.configValue("onInput"));
      }

      this.$emit("input", this.newValue);
    },
    filter() {
      this.filters.forEach(filter => {
        switch (filter) {
          case "trim":
            if (!this.newValue) {
              return;
            }
            this.newValue = this.newValue.trim();
            break;
        }
      });
    }
  },
  computed: {
    componentClasses() {
      return this.configValue("classes", []);
    },
    min() {
      let min = this.configValue("min");
      this.configValue("validations", []).forEach(validation => {
        let validationParts = validation.split(":");
        if (validationParts[0] === "min") {
          min = validationParts[1];
        }
      });

      return min;
    },
    max() {
      let max = this.configValue("max");
      this.configValue("validations", []).forEach(validation => {
        let validationParts = validation.split(":");
        if (validationParts[0] === "max") {
          max = validationParts[1];
        }
      });

      return max;
    },
    label() {
      let label = this.configValue("label");

      if (!label) {
        return;
      }

      if (this.isRequired) {
        label = `${label} (required)`;
      }

      return label;
    },
    type() {
      return this.configValue("type");
    },
    isRequired() {
      return this.configValue("required", false);
    },
    filters() {
      return this.configValue("filters", []);
    },
    isErrorsArray() {
      return this.errors instanceof Array;
    }
  },
  created() {
    this.newValue = this.value;
  },
  mounted() {},
  beforeDestroy() {},
  mixins: [],
  components: {
    PageButton,
    FormsFile,
    FormsTextArea,
    FormsTextField,
    FormsSelect,
    FormsRadio,
    FormsCheckbox,
    FormsCheckboxSingle,
    FormsAddress,
    FormsBoolean,
    OktaPasswordPolicy,
    HtmlComponent,
    StepperComponent,
    StripeCreditCard,
    OrderStatus,
    OrderCancellationRationalesSelector,
    DeliverySchedule,
    SchedulePickupDropoff
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.errors {
  color: red;
  margin: -1em 0.5em 1em 0;
  font-size: 0.85em;
  min-height: 14px;
}
</style>
